import React, { useState, useEffect } from "react";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  TrendingDown,
  TrendingUp,
  Trash2,
  Zap,
  Clock,
  Target,
  CalendarDays,
  Sparkles,
  Scale,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import telegramService from "../utils/telegram";
import apiService from "../services/api";
import WeightModal from "./WeightModal";
import ShimmerSkeleton from "./ui/ShimmerSkeleton";
import { useToast } from "./ui/Toast";
import { useChartColors } from "../hooks/useChartColors";
import { Meal, AIProgressAnalysis, ProgressPhoto } from "../types";
import ProgressPhotos from "./ProgressPhotos";
import MoodTrend from "./MoodTrend";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const Stats: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const chartColors = useChartColors();
  const pieColors = [chartColors.brand, chartColors.coral, chartColors.warning, chartColors.info];

  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [currentWeight, setCurrentWeight] = useState(0);
  const [targetWeight, setTargetWeight] = useState(65);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [trendDirection, setTrendDirection] = useState<"up" | "down" | "flat">("flat");
  const [weeklyChange, setWeeklyChange] = useState(0);

  const [recentMeals, setRecentMeals] = useState<Meal[]>([]);
  const [analytics, setAnalytics] = useState<{
    calorieDistribution: { name: string; value: number }[];
    insight: string;
  } | null>(null);
  const [predictionDays, setPredictionDays] = useState<number | null>(null);
  const [dailyAvgCalories, setDailyAvgCalories] = useState(0);
  const [progressAnalysis, setProgressAnalysis] = useState<AIProgressAnalysis | null>(null);
  const [refreshingAnalysis, setRefreshingAnalysis] = useState(false);
  const [userGoal, setUserGoal] = useState<string>("");
  const [startWeight, setStartWeight] = useState(0);
  const [progressPhotos, setProgressPhotos] = useState<ProgressPhoto[]>([]);
  const [photoComparison, setPhotoComparison] = useState<{
    before: ProgressPhoto;
    after: ProgressPhoto;
  } | null>(null);
  const [isPremium, setIsPremium] = useState(false);

  const fetchStats = async () => {
    try {
      const tgId = telegramService.getUserId();
      if (!tgId) return;

      const [user, history, meals, analyticsData, stats7Days] = await Promise.all([
        apiService.getUser(tgId),
        apiService.getWeightHistory(tgId),
        apiService.getRecentMeals(tgId),
        apiService.getAnalytics(tgId),
        apiService.getLast7DaysStats(tgId),
      ]);

      setTargetWeight(user.targetWeight || 65);
      setCurrentWeight(user.weight || 0);
      setUserGoal(user.goal || "");
      setStartWeight(history.length > 0 ? history[0].weight : user.weight || 0);

      const data = processWeightData(history);
      setChartData(data);
      calculateTrend(data);

      if (data.length >= 7 && user.weight) {
        const currentMA = data[data.length - 1].ma7;
        const pastMA = data[data.length - 8]?.ma7 || currentMA;
        const diff = currentMA - pastMA;

        if (diff < -0.1 && user.weight > targetWeight) {
          const ratePerWeek = Math.abs(diff);
          const remaining = user.weight - targetWeight;
          const weeks = remaining / ratePerWeek;
          setPredictionDays(Math.round(weeks * 7));
        } else {
          setPredictionDays(null);
        }
      }

      setRecentMeals(meals);
      setAnalytics(analyticsData);

      const validDays = stats7Days.filter((d) => d.totalCalories > 0);
      const avg = validDays.length
        ? Math.round(
            validDays.reduce((acc, curr) => acc + curr.totalCalories, 0) / validDays.length,
          )
        : 0;
      setDailyAvgCalories(avg);

      try {
        const subData = await apiService.getSubscription(tgId);
        setIsPremium(subData?.isPremium || false);
      } catch {
        // Optional
      }

      try {
        const [photos, comp] = await Promise.all([
          apiService.getProgressPhotos(tgId),
          apiService.getPhotoComparison(tgId),
        ]);
        setProgressPhotos(photos);
        setPhotoComparison(comp);
      } catch {
        // Photos are optional
      }

      try {
        const analysis = await apiService.getProgressAnalysis(tgId, i18n.language);
        setProgressAnalysis(analysis);
      } catch {
        // Analysis is optional
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAnalysis = async () => {
    setRefreshingAnalysis(true);
    try {
      const tgId = telegramService.getUserId();
      if (!tgId) return;
      const analysis = await apiService.getProgressAnalysis(tgId, i18n.language, true);
      setProgressAnalysis(analysis);
    } catch {
      // Ignore
    } finally {
      setRefreshingAnalysis(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processWeightData = (history: { weight: number; date: string }[]) => {
    if (!history.length) return [];
    const sorted = [...history].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
    return sorted.map((entry, index, array) => {
      const windowStart = Math.max(0, index - 6);
      const windowSlice = array.slice(windowStart, index + 1);
      const sum = windowSlice.reduce((acc, curr) => acc + curr.weight, 0);
      const avg = sum / windowSlice.length;
      return {
        date: new Date(entry.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        weight: entry.weight,
        ma7: parseFloat(avg.toFixed(1)),
      };
    });
  };

  const calculateTrend = (data: any[]) => {
    if (data.length < 8) {
      setTrendDirection("flat");
      return;
    }
    const currentMA = data[data.length - 1].ma7;
    const pastMA = data[data.length - 8].ma7;
    const diff = currentMA - pastMA;
    setWeeklyChange(parseFloat(Math.abs(diff).toFixed(1)));
    if (diff < -0.1) setTrendDirection("down");
    else if (diff > 0.1) setTrendDirection("up");
    else setTrendDirection("flat");
  };

  const handleDeleteMeal = async (mealId: string) => {
    const confirmed = await telegramService.showConfirm(t("mealHistory.deleteConfirm"));
    if (confirmed) {
      try {
        const tgId = telegramService.getUserId();
        if (tgId) {
          await apiService.deleteMeal(mealId, tgId);
          setRecentMeals((prev) => prev.filter((m) => m._id !== mealId));
          telegramService.haptic("success");
          toast.show(t("stats.mealDeleted"), "success");
        }
      } catch (e) {
        console.error(e);
        telegramService.haptic("error");
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  if (loading) return <ShimmerSkeleton variant="stats" />;

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen gradient-cream pb-32 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="fixed top-20 left-[-20%] w-[50%] h-[50%] bg-info/10 rounded-full blur-[100px] pointer-events-none" />

      {/* Header */}
      <div className="bg-surface-elevated backdrop-blur-md pb-4 px-4 sticky top-0 z-10 border-b border-line shadow-card flex items-center justify-between pt-[calc(1.5rem+var(--safe-top))]">
        <h1 className="text-xl font-black text-content-primary flex items-center gap-2">
          <CalendarDays size={20} className="text-brand-500" />
          {t("stats.title")}
        </h1>
        <button
          onClick={() => {
            telegramService.haptic("light");
            setShowWeightModal(true);
          }}
          className="px-3 py-1.5 bg-content-primary text-content-inverse text-xs font-bold rounded-lg shadow-md active:scale-95 transition-all flex items-center gap-1"
        >
          <Scale size={14} /> {t("stats.logWeight")}
        </button>
      </div>

      <div className="px-4 space-y-4 py-4">
        {/* Main Trend Chart */}
        <motion.div variants={itemVariants} className="card p-5 relative overflow-hidden">
          <div className="flex justify-between items-start mb-6">
            <div>
              <span className="text-xs font-bold text-content-tertiary uppercase tracking-wider">
                {t("stats.currentWeight")}
              </span>
              <div className="flex items-end gap-2 mt-1">
                <span className="text-3xl font-black text-content-primary tracking-tight">
                  {currentWeight}
                </span>
                <span className="text-sm font-bold text-content-tertiary mb-1.5">kg</span>
              </div>
            </div>

            <div
              className={`px-3 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 ${
                trendDirection === "down"
                  ? "bg-success-soft text-success"
                  : trendDirection === "up"
                    ? "bg-danger-soft text-danger"
                    : "bg-inset text-content-secondary"
              }`}
            >
              {trendDirection === "down" && <TrendingDown size={14} />}
              {trendDirection === "up" && <TrendingUp size={14} />}
              {trendDirection === "flat"
                ? t("stats.stable")
                : `${weeklyChange} ${t("stats.kgPerWeek")}`}
            </div>
          </div>

          <div className="h-56 w-full -ml-4">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="colorWeight" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartColors.info} stopOpacity={0.1} />
                    <stop offset="95%" stopColor={chartColors.info} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.grid} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: chartColors.textTertiary }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={30}
                  dy={10}
                />
                <YAxis
                  domain={["dataMin - 1", "dataMax + 1"]}
                  tick={{ fontSize: 10, fill: chartColors.textTertiary }}
                  axisLine={false}
                  tickLine={false}
                  width={35}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "16px",
                    border: "none",
                    boxShadow: "0 10px 25px -5px rgb(0 0 0 / 0.1)",
                    backgroundColor: chartColors.surface,
                    padding: "12px",
                  }}
                  itemStyle={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: chartColors.text,
                  }}
                  labelStyle={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: chartColors.textTertiary,
                    marginBottom: "4px",
                  }}
                />
                <ReferenceLine
                  y={targetWeight}
                  stroke={chartColors.success}
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                />
                <Area type="monotone" dataKey="weight" stroke="none" fill="url(#colorWeight)" />
                <Line
                  type="monotone"
                  dataKey="ma7"
                  stroke={chartColors.info}
                  strokeWidth={3}
                  dot={false}
                  isAnimationActive={true}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Progress Photos */}
        <motion.div variants={itemVariants}>
          <ProgressPhotos
            photos={progressPhotos}
            comparison={photoComparison}
            onRefresh={fetchStats}
          />
        </motion.div>

        {/* Mood Trend */}
        <motion.div variants={itemVariants}>
          <MoodTrend isPremium={isPremium} />
        </motion.div>

        {/* Smart Analytics Cards Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Prediction Card */}
          <motion.div variants={itemVariants} className="card p-4 relative overflow-hidden">
            <div className="absolute -right-2 -top-2 text-premium-soft opacity-70">
              <Target size={60} />
            </div>

            <div className="relative z-10">
              <span className="text-[10px] font-bold text-premium uppercase tracking-wider">
                {t("stats.forecast")}
              </span>
              <div className="mt-2 h-16 flex flex-col justify-end">
                {progressAnalysis && progressAnalysis.estimatedWeeks > 0 ? (
                  <>
                    <div className="text-3xl font-black text-premium leading-none">
                      ~{progressAnalysis.estimatedWeeks}
                    </div>
                    <div className="text-xs font-medium text-content-secondary">
                      {t("aiAnalysis.weeks")} 🎯
                    </div>
                  </>
                ) : predictionDays ? (
                  <>
                    <div className="text-3xl font-black text-premium leading-none">
                      {predictionDays}
                    </div>
                    <div className="text-xs font-medium text-content-secondary">
                      {t("stats.daysToGo")} 🎯
                    </div>
                  </>
                ) : (
                  <span className="text-xs font-medium text-content-tertiary">
                    {t("stats.keepLogging")}
                  </span>
                )}
              </div>
            </div>
          </motion.div>

          {/* Daily Avg Card */}
          <motion.div variants={itemVariants} className="card p-4 relative overflow-hidden">
            <div className="absolute -right-2 -top-2 text-brand-soft opacity-70">
              <Zap size={60} />
            </div>
            <div className="relative z-10">
              <span className="text-[10px] font-bold text-brand-text uppercase tracking-wider">
                {t("stats.avgIntake")}
              </span>
              <div className="mt-2 h-16 flex flex-col justify-end">
                <div className="text-3xl font-black text-brand-500 leading-none">
                  {dailyAvgCalories}
                </div>
                <div className="text-xs font-medium text-content-secondary">
                  {t("stats.kcalPerDay")}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* AI Progress Milestones */}
        {progressAnalysis &&
          progressAnalysis.milestones &&
          progressAnalysis.milestones.length > 0 &&
          userGoal !== "maintain" && (
            <motion.div variants={itemVariants} className="card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-content-primary">
                  {t("aiAnalysis.progressToGoal")}
                </h3>
                <button
                  onClick={handleRefreshAnalysis}
                  disabled={refreshingAnalysis}
                  className="text-[10px] font-bold text-success disabled:opacity-50"
                >
                  {refreshingAnalysis ? "..." : t("aiAnalysis.refreshAnalysis")}
                </button>
              </div>

              {/* Progress bar */}
              {(() => {
                const totalChange = Math.abs(startWeight - targetWeight);
                const currentChange = Math.abs(startWeight - currentWeight);
                const progressPercent =
                  totalChange > 0 ? Math.min(100, (currentChange / totalChange) * 100) : 0;
                return (
                  <div className="mb-4">
                    <div className="flex justify-between text-[10px] text-content-tertiary font-medium mb-1">
                      <span>{startWeight} kg</span>
                      <span>{targetWeight} kg</span>
                    </div>
                    <div className="h-2 bg-inset rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-success rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercent}%` }}
                        transition={{ duration: 1 }}
                      />
                    </div>
                    <div className="text-center mt-1">
                      <span className="text-xs font-bold text-success">
                        {Math.round(progressPercent)}%
                      </span>
                    </div>
                  </div>
                );
              })()}

              {/* Milestones */}
              <div className="space-y-2">
                {progressAnalysis.milestones.map((m, i) => {
                  const reached =
                    userGoal === "lose_weight"
                      ? currentWeight <= m.targetWeight
                      : currentWeight >= m.targetWeight;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                          reached ? "bg-success text-white" : "bg-inset text-content-tertiary"
                        }`}
                      >
                        {reached ? "✓" : `${m.percentage}%`}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-content-secondary">
                          {m.targetWeight} kg
                        </span>
                      </div>
                      <span className="text-[10px] text-content-tertiary">{m.estimatedDate}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

        {/* AI Recommendations */}
        {progressAnalysis &&
          progressAnalysis.recommendations &&
          progressAnalysis.recommendations.length > 0 && (
            <motion.div
              variants={itemVariants}
              className="relative p-5 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-16 -mt-16 pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={16} className="text-yellow-300" />
                  <h3 className="text-sm font-bold text-white/90">
                    {t("aiAnalysis.recommendations")}
                  </h3>
                </div>
                <div className="space-y-2">
                  {progressAnalysis.recommendations.map((rec, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                      <p className="text-xs leading-relaxed text-indigo-100 font-medium">{rec}</p>
                    </div>
                  ))}
                </div>
                {progressAnalysis.weeklyPlan && (
                  <div className="mt-3 pt-3 border-t border-white/20">
                    <p className="text-[10px] font-bold text-white/60 uppercase tracking-wider mb-1">
                      {t("aiAnalysis.weeklyPlan")}
                    </p>
                    <p className="text-xs text-indigo-100 leading-relaxed">
                      {progressAnalysis.weeklyPlan}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

        {/* Insights & Calorie Source */}
        <div className="grid grid-cols-1 gap-4">
          {/* Calorie Source Pie */}
          {analytics && analytics.calorieDistribution.length > 0 && (
            <motion.div
              variants={itemVariants}
              className="card p-5 flex items-center justify-between"
            >
              <div className="w-1/2">
                <h3 className="font-bold text-content-primary mb-3 text-sm">
                  {t("stats.topSources")}
                </h3>
                <div className="space-y-2">
                  {analytics.calorieDistribution.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: pieColors[index % pieColors.length] }}
                      />
                      <span className="text-xs text-content-secondary font-medium truncate">
                        {entry.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-32 h-32 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics.calorieDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={55}
                      paddingAngle={5}
                      dataKey="value"
                      stroke="none"
                    >
                      {analytics.calorieDistribution.map((_entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={pieColors[index % pieColors.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-xs font-bold text-content-tertiary">CAL</div>
                </div>
              </div>
            </motion.div>
          )}

          {/* AI Insight */}
          {analytics?.insight && (
            <motion.div
              variants={itemVariants}
              className="relative p-5 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-16 -mt-16 pointer-events-none" />

              <div className="flex items-start gap-3 relative z-10">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm shrink-0">
                  <Sparkles size={20} className="text-yellow-300" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white/90 mb-1">{t("stats.aiAnalysis")}</h3>
                  <p className="text-xs leading-relaxed text-indigo-100 font-medium opacity-90">
                    {analytics.insight}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Recent Meals List */}
        <motion.div variants={itemVariants} className="space-y-4 pt-4">
          <h2 className="text-lg font-black text-content-primary px-1">{t("stats.logHistory")}</h2>

          <div className="space-y-3">
            <AnimatePresence>
              {recentMeals.map((meal) => (
                <motion.div
                  key={meal._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  className="card p-3 flex items-center justify-between group active:scale-[0.99] transition-transform"
                >
                  <div className="flex items-center gap-4 overflow-hidden">
                    <div className="w-14 h-14 rounded-xl bg-inset shrink-0 overflow-hidden relative shadow-inner">
                      {meal.imageUrl ? (
                        <img
                          src={meal.imageUrl}
                          alt={meal.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xl">
                          🍽️
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-content-primary text-sm truncate">
                        {meal.name}
                      </h4>
                      <div className="flex items-center gap-3 text-[10px] text-content-tertiary font-medium mt-1">
                        <span className="flex items-center gap-1">
                          <Clock size={10} /> {new Date(meal.timestamp).toLocaleDateString()}
                        </span>
                        <span className="text-success font-bold bg-success-soft px-1.5 py-0.5 rounded">
                          {meal.calories} kcal
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleDeleteMeal(meal._id)}
                    className="p-3 text-content-tertiary hover:text-danger hover:bg-danger-soft rounded-full transition-all"
                  >
                    <Trash2 size={18} />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>

            {recentMeals.length === 0 && (
              <div className="text-center p-10 bg-surface rounded-3xl border border-dashed border-line-strong">
                <div className="text-4xl mb-3 opacity-50">📝</div>
                <p className="text-content-tertiary text-sm font-medium">{t("home.noMeals")}</p>
              </div>
            )}
          </div>
        </motion.div>

        {showWeightModal && (
          <WeightModal
            currentWeight={currentWeight}
            onSave={async (w) => {
              const tgId = telegramService.getUserId();
              if (tgId) {
                await apiService.updateProfile(tgId, { weight: w });
              }
              telegramService.haptic("success");
              setShowWeightModal(false);
              fetchStats();
            }}
            onClose={() => setShowWeightModal(false)}
          />
        )}
      </div>
    </motion.div>
  );
};

export default Stats;
